import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  Input,
  output,
  signal,
  ViewChild,
  inject,
} from '@angular/core';
import { UploadDropzoneComponent } from '../upload-dropzone/upload-dropzone.component';
import { ReactiveFormsModule } from '@angular/forms';
import { AlertsComponent } from '../../../alert/components/alerts/alerts.component';
import { AlertsService } from '../../../alert/services/alerts.service';
import { ProgressBarComponent } from '../../../progress-bar/components/progress-bar/progress-bar.component';
import { UploadEvent } from '../../events/upload.event';
import { finalize, Observable } from 'rxjs';
import { FileUploadClearEvent } from '../../events/file-upload-clear.event';
import { FileEvent, isNil, Nillable, once } from '@web/utils';
import { UploadException } from '../../exceptions/upload.exception';
import { UploadValidationResult } from '../../models/upload-validation-result';
import { InvalidSizeException } from '../../exceptions/invalid-size.exception';
import { InvalidFileTypeException } from '../../exceptions/invalid-file-type.exception';
import { UploadValidationResultCollection } from '../../models/upload-validation-result-collection';
import { NgTemplateOutlet } from '@angular/common';
import { DropzoneTemplateRef } from '../../types/dropzone-template-ref';
import { fileSize } from '../../../file-size/utils/file-size';

@Component({
  selector: 'app-upload',
  imports: [
    UploadDropzoneComponent,
    ReactiveFormsModule,
    AlertsComponent,
    NgTemplateOutlet,
    ProgressBarComponent,
  ],
  providers: [AlertsService],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadComponent {
  private readonly alertsService = inject(AlertsService);

  public readonly dropzoneTemplateRef = input<DropzoneTemplateRef>();
  public readonly disabled = input(false);
  public readonly allowedMimeTypes = input('');
  public readonly maxFileSize = input(2 * 1000 * 1000);
  public readonly multi = input(true);
  public readonly maxNumberOfFiles = input(0);
  public readonly clearFileEvent = output<FileUploadClearEvent>();

  @ViewChild('fileInput', { static: false })
  public fileInput!: ElementRef<HTMLInputElement>;

  /* prettier-ignore */
  @Input({ required: true })
  public uploadFn!: (event: UploadEvent) => Observable<void>;

  protected readonly error = signal<Nillable<string>>(null);
  protected readonly dragover = signal(false);
  protected readonly pending = signal(false);
  protected readonly multiple = computed(() => (this.multi() ? '' : null));

  public handleDragleave(): void {
    this.dragover.set(false);
  }

  public handleDragover(): void {
    this.dragover.set(true);
  }

  public handleDragdrop(): void {
    this.dragover.set(false);
  }

  public handleChange(event: Event): void {
    const fileEvent = event as FileEvent;
    const files = this.selectedFilesFromEvent(fileEvent);

    this.clearErrorMessages();

    if (isNil(files)) {
      return;
    }

    if (!this.multi()) {
      this.singleUpload(files);
    } else {
      this.multiUpload(files);
    }
  }

  public reset(): void {
    this.clearFileInput();
    this.clearFile();
  }

  public showChooseFileDialog(): void {
    this.fileInput.nativeElement.click();
  }

  private singleUpload(files: File[]): void {
    const [file] = files;
    const result = this.validateSingleFile(file);
    const filesToUpload = [result.file];

    if (!result.isValid) {
      const reason = result.reason!;

      this.showSingleErrorMessage(reason);

      return;
    }

    this.upload(filesToUpload);
  }

  private multiUpload(files: File[]): void {
    if (!this.validateFilesCount(files)) {
      this.showSingleErrorMessage(
        `Maksymalna ilość przesyłanych plików wynosi ${this.maxNumberOfFiles()}.`,
      );

      return;
    }

    const results = this.validateMultipleFiles(files);
    const filesToUpload = results.validFiles();

    if (results.haveInvalid()) {
      const reasons = results.invalids().map(({ reason }) => reason!);

      this.showMultipleErrorMessages(reasons);
    }

    this.upload(filesToUpload);
  }

  private validateFilesCount(files: File[]): boolean {
    if (this.maxNumberOfFiles() === 0) {
      return true;
    }

    return files.length <= this.maxNumberOfFiles();
  }

  private upload(files: File[]): void {
    this.markAsPending();
    this.uploadFn(new UploadEvent(files))
      .pipe(
        once(),
        finalize(() => {
          this.markAsResolved();
          this.reset();
        }),
      )
      .subscribe({
        error: (error) => {
          if (UploadException.isInstance(error)) {
            const { message } = error;

            this.showSingleErrorMessage(message);

            return;
          }
        },
      });
  }

  private clearErrorMessages(): void {
    this.alertsService.clear();
  }

  private showMultipleErrorMessages(messages: string[]): void {
    this.alertsService.showMultiple((builder) =>
      messages.map((reason) => builder.initFailure(reason).build()),
    );
  }

  private showSingleErrorMessage(message: string): void {
    this.alertsService.showSingle((builder) =>
      builder.initFailure(message).build(),
    );
  }

  private showSuccessMessage(message: string): void {
    this.alertsService.showSingle((builder) =>
      builder.initSuccess(message).build(),
    );
  }

  private selectedFilesFromEvent(event: FileEvent): Nillable<File[]> {
    const { files } = event.target;

    if (!files) {
      return;
    }

    return Array.from(files);
  }

  private validateSingleFile(file: File): UploadValidationResult {
    try {
      this.validateMimeType(file);
      this.validateFileSize(file);

      return UploadValidationResult.valid(file);
    } catch (error) {
      if (InvalidSizeException.isInstance(error)) {
        const { message } = error as InvalidSizeException;

        return UploadValidationResult.invalid(file, message);
      }

      if (InvalidFileTypeException.isInstance(error)) {
        const { message } = error as InvalidFileTypeException;

        return UploadValidationResult.invalid(file, message);
      }

      return UploadValidationResult.invalid(
        file,
        `Dla pliku ${file.name} wystąpił nieznany błąd. Proszę skontaktować się z administratorem w celu rozwiązania problemu.`,
      );
    }
  }

  private validateMultipleFiles(
    files: File[],
  ): UploadValidationResultCollection {
    return new UploadValidationResultCollection(
      files.map((file) => this.validateSingleFile(file)),
    );
  }

  private validateMimeType(file: File): void {
    if (!this.allowedMimeTypes()) {
      return;
    }

    const acceptableMimeTypes = this.allowedMimeTypes()
      .split(',')
      .map((type) => type.trim());

    for (const mimeType of acceptableMimeTypes) {
      const acceptable = this.isAcceptableMimeType(mimeType, file);

      if (acceptable) {
        return;
      }
    }

    throw new InvalidFileTypeException(
      `Nieobsługiwany typ pliku. Obsługiwane typy to: ${this.allowedMimeTypes()}.`,
    );
  }

  private validateFileSize(file: File): void {
    if (!this.maxFileSize()) {
      return;
    }

    const validSize = file.size <= this.maxFileSize();

    if (validSize) {
      return;
    }

    throw new InvalidSizeException(
      `Rozmiar pliku "${file.name}" został przekroczony. Maksymalny rozmiar pliku to: ${fileSize(this.maxFileSize())}.`,
    );
  }

  private isAcceptableMimeType(type: string, file: File): boolean {
    return this.isWildcard(type)
      ? this.typeClass(file.type) === this.typeClass(type)
      : file.type === type ||
          this.fileExtension(file).toLowerCase() === type.toLowerCase();
  }

  private typeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  private isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  private fileExtension(file: File): string {
    return '.' + file.name.split('.').pop();
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private clearFileInput(): void {
    this.fileInput.nativeElement.value = '';
  }

  private clearFile(): void {
    this.clearFileEvent.emit(new FileUploadClearEvent());
  }
}
